import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Mail,
  Search,
  Trash2,
  RefreshCw,
  ArrowLeft,
  Inbox,
  Loader2,
} from "lucide-react";
import { useAuth, authHeader } from "@/context/AuthContext";
import useSeo from "@/hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminLeadsPage() {
  useSeo({ title: "Anfragen", path: "/admin/leads", noindex: true });
  const { user, logout } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/leads`, {
        headers: authHeader(),
      });
      setLeads(data);
      if (data.length && !selectedId) setSelectedId(data[0].id);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
      } else {
        toast.error("Anfragen konnten nicht geladen werden.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Anfrage wirklich löschen?")) return;
    try {
      await axios.delete(`${API}/leads/${id}`, { headers: authHeader() });
      setLeads((l) => l.filter((x) => x.id !== id));
      if (selectedId === id) setSelectedId(null);
      toast.success("Anfrage gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return leads;
    return leads.filter(
      (l) =>
        l.name.toLowerCase().includes(q) ||
        l.email.toLowerCase().includes(q) ||
        l.message.toLowerCase().includes(q)
    );
  }, [leads, query]);

  const selected = leads.find((l) => l.id === selectedId);

  return (
    <main
      data-testid="admin-leads-page"
      className="min-h-screen bg-[#F7F9FB] text-[#1F2937]"
    >
      {/* Top bar */}
      <header className="sticky top-0 z-30 bg-[#F7F9FB]/90 backdrop-blur-md border-b border-[#1F2937]/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm text-[#1F2937]/65 hover:text-[#2E89C4] transition"
            >
              <ArrowLeft size={16} />
              <span className="hidden sm:inline">Startseite</span>
            </Link>
            <span className="h-5 w-px bg-[#1F2937]/15 hidden sm:block" />
            <div className="flex items-center gap-2">
              <span className="inline-block h-2.5 w-2.5 rounded-full bg-[#2E89C4]" />
              <span className="font-display font-bold tracking-tight">
                Anfragen
              </span>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-[#1F2937]/55">
              {user?.email}
            </span>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-full border border-[#1F2937]/15 hover:border-[#2E89C4] hover:text-[#2E89C4] transition"
            >
              <LogOut size={14} />
              Abmelden
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-6">
          <div>
            <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight">
              Erstgespräch-Anfragen
            </h1>
            <p className="text-sm text-[#1F2937]/60 mt-1">
              {leads.length}{" "}
              {leads.length === 1 ? "Anfrage" : "Anfragen"} insgesamt
            </p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search
                size={14}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1F2937]/40"
              />
              <input
                data-testid="admin-leads-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Suchen…"
                className="pl-9 pr-3 py-2.5 rounded-full bg-white/70 border border-[#1F2937]/15 text-sm focus:outline-none focus:border-[#2E89C4]"
              />
            </div>
            <button
              data-testid="admin-leads-refresh"
              onClick={load}
              className="h-10 w-10 rounded-full border border-[#1F2937]/15 hover:border-[#2E89C4] hover:text-[#2E89C4] transition flex items-center justify-center"
              aria-label="Aktualisieren"
            >
              <RefreshCw size={14} />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-[#1F2937]/55">
            <Loader2 className="animate-spin" />
          </div>
        ) : leads.length === 0 ? (
          <div
            data-testid="admin-leads-empty"
            className="rounded-2xl border border-dashed border-[#1F2937]/15 bg-white/40 py-20 text-center"
          >
            <Inbox className="mx-auto text-[#1F2937]/35 mb-3" />
            <p className="font-display text-lg font-bold">
              Noch keine Anfragen
            </p>
            <p className="text-sm text-[#1F2937]/55 mt-1">
              Sobald jemand das Kontaktformular auf der Landingpage abschickt,
              erscheint die Anfrage hier.
            </p>
          </div>
        ) : (
          <div className="grid lg:grid-cols-12 gap-6">
            {/* List */}
            <div className="lg:col-span-5 xl:col-span-4 space-y-2">
              {filtered.map((l) => {
                const active = selectedId === l.id;
                return (
                  <button
                    key={l.id}
                    data-testid={`admin-lead-row-${l.id}`}
                    onClick={() => setSelectedId(l.id)}
                    className={`w-full text-left rounded-xl p-4 border transition ${
                      active
                        ? "border-[#2E89C4] bg-white shadow-[0_10px_30px_-15px_rgba(201,96,42,0.4)]"
                        : "border-[#1F2937]/10 bg-white/60 hover:border-[#1F2937]/25"
                    }`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="font-display font-bold text-sm truncate">
                          {l.name}
                        </p>
                        <p className="text-xs text-[#2E89C4] truncate">
                          {l.email}
                        </p>
                      </div>
                      <span className="text-[10px] text-[#1F2937]/45 shrink-0 whitespace-nowrap">
                        {formatDate(l.created_at)}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-[#1F2937]/65 line-clamp-2">
                      {l.message}
                    </p>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <p className="text-sm text-[#1F2937]/55 text-center py-8">
                  Keine Treffer für „{query}".
                </p>
              )}
            </div>

            {/* Detail */}
            <div className="lg:col-span-7 xl:col-span-8">
              {selected ? (
                <article
                  data-testid="admin-lead-detail"
                  className="rounded-2xl border border-[#1F2937]/10 bg-white p-7 lg:p-9"
                >
                  <div className="flex items-start justify-between gap-4 mb-6">
                    <div>
                      <p className="text-[11px] tracking-[0.18em] uppercase text-[#6FB04F] font-semibold mb-1.5">
                        Eingegangen am {formatDate(selected.created_at)}
                      </p>
                      <h2 className="font-display text-2xl lg:text-3xl font-extrabold">
                        {selected.name}
                      </h2>
                      <a
                        href={`mailto:${selected.email}`}
                        className="inline-flex items-center gap-1.5 text-[#2E89C4] hover:underline text-sm mt-1.5"
                      >
                        <Mail size={14} />
                        {selected.email}
                      </a>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <a
                        href={`mailto:${selected.email}?subject=Re:%20Deine%20Anfrage%20bei%20Alex%20Buchner%20Health`}
                        className="btn-primary inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-semibold"
                      >
                        <Mail size={14} />
                        Antworten
                      </a>
                      <button
                        data-testid={`admin-lead-delete-${selected.id}`}
                        onClick={() => onDelete(selected.id)}
                        className="h-10 w-10 rounded-full border border-[#1F2937]/15 hover:border-red-500 hover:text-red-500 transition flex items-center justify-center"
                        aria-label="Löschen"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>

                  <div className="border-t border-[#1F2937]/10 pt-6">
                    <p className="text-xs font-semibold tracking-wide uppercase text-[#1F2937]/55 mb-3">
                      Nachricht
                    </p>
                    <p className="text-[15px] leading-relaxed whitespace-pre-wrap text-[#1F2937]/85">
                      {selected.message}
                    </p>
                  </div>
                </article>
              ) : (
                <div className="rounded-2xl border border-dashed border-[#1F2937]/15 bg-white/40 py-20 text-center">
                  <p className="text-sm text-[#1F2937]/55">
                    Wähle eine Anfrage aus der Liste links.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}
